import React, { useState } from 'react';
import { Heart, Film, Tv, Music, PlayCircle, Clock, Youtube } from 'lucide-react';

const SORA = "'Sora', system-ui, sans-serif";
const MANROPE = "'Manrope', system-ui, sans-serif";

const ActivityPanel = ({ activities = [], compact = false, limit = 10 }) => {
  const [hoveredActivity, setHoveredActivity] = useState(null);

  const getContentStyle = (contentType) => {
    switch (contentType) {
      case 'movie': return { icon: Film, color: '#3B82F6' };
      case 'tv': return { icon: Tv, color: '#8B5CF6' };
      case 'music': return { icon: Music, color: '#EC4899' };
      case 'anime': return { icon: PlayCircle, color: '#F59E0B' };
      case 'reaction': return { icon: PlayCircle, color: '#8B5CF6' };
      case 'channel': return { icon: Youtube, color: '#EF4444' };
      default: return { icon: Heart, color: '#EC4899' };
    }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, fontFamily: MANROPE }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
        <Clock size={16} color="#c4b5fd" />
        <span style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: '700', fontFamily: SORA }}>Recent Activity</span>
        <span style={{ color: '#6f6b7d', fontSize: '12px' }}>({activities.length})</span>
      </div>

      {/* Activity List */}
      {activities.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: 0, overflowY: 'auto', paddingRight: '2px' }}>
          {activities.slice(0, limit).map((activity, index) => {
            const style = getContentStyle(activity.contentType);
            const Icon = style.icon;
            const isHovered = hoveredActivity === index;

            return (
              <div
                key={activity.id || index}
                style={{
                  display: 'flex', gap: '10px', padding: '10px',
                  background: isHovered ? `${style.color}10` : 'transparent',
                  borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s ease',
                }}
                onMouseEnter={() => setHoveredActivity(index)}
                onMouseLeave={() => setHoveredActivity(null)}
                onClick={() => activity.url && window.open(activity.url, '_blank')}
              >
                {/* Thumbnail */}
                <div style={{
                  width: activity.contentType === 'channel' ? '36px' : '32px',
                  height: activity.contentType === 'channel' ? '36px' : '48px',
                  borderRadius: activity.contentType === 'channel' ? '50%' : '6px',
                  overflow: 'hidden', flexShrink: 0, background: '#1F2937',
                }}>
                  {activity.poster ? (
                    <img src={activity.poster} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={16} color={style.color} />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    color: '#FFFFFF', fontSize: '12px', fontWeight: '500', margin: 0, marginBottom: '2px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {activity.title}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{
                      fontSize: '9px', fontWeight: '600', color: style.color,
                      textTransform: 'uppercase', background: `${style.color}20`,
                      padding: '1px 5px', borderRadius: '4px',
                    }}>
                      {activity.contentType}
                    </span>
                    <span style={{ color: '#6f6b7d', fontSize: '10px' }}>
                      {formatTime(activity.timestamp || activity.likedAt)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '30px 10px' }}>
          <Clock size={24} color="#6f6b7d" style={{ opacity: 0.5, marginBottom: '8px' }} />
          <p style={{ color: '#6f6b7d', fontSize: '12px', margin: 0 }}>No activity yet</p>
        </div>
      )}
    </div>
  );
};

const formatTime = (dateString) => {
  if (!dateString) return 'recently';
  const diffMinutes = Math.floor((Date.now() - new Date(dateString)) / 60000);
  if (diffMinutes < 1) return 'now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
  return `${Math.floor(diffMinutes / 1440)}d ago`;
};

export default ActivityPanel;
